import { ReactNode } from "react";
import { Drawer } from "@mui/material";
import { Typography, IconButton } from "@kaltura/ds-react-components";
import { Close16Icon } from "@kaltura/ds-react-icons";
import { Stack } from "@kaltura/mediaspace-shared-styled";
import { fullWidthFlexItemStyles, translate } from "@kaltura/mediaspace-shared-utils";
import styled from "@emotion/styled";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface BaseInstructionsDrawerProps {
    open: boolean;
    onClose: () => void;
    appName: string;
}

interface InstructionsDrawerProps extends BaseInstructionsDrawerProps {
    title: string;
    logo: ReactNode;
    children: ReactNode;
    /** Analytics action name (var2) of the close button */
    closeAnalyticsName: string;
}

/**
 * Bottom drawer with instructions.
 */
export const InstructionsDrawer = ({
    open,
    onClose,
    appName,
    title,
    logo,
    children,
    closeAnalyticsName,
}: InstructionsDrawerProps) => {
    const sendButtonAnalytics = useButtonAnalytics();

    const handleClose = () => {
        sendButtonAnalytics(closeAnalyticsName, ButtonClickAnalyticsType.CLOSE);
        onClose();
    };

    return (
        <Drawer
            anchor={"bottom"}
            open={open}
            onClose={handleClose}
            slotProps={{ paper: { sx: { borderRadius: "16px 16px 0 0" } } }}
        >
            <StyledHeader>
                <Typography variant={"h5"} component={"div"}>
                    {title}
                </Typography>
                <IconButton
                    variant={"borderless"}
                    color={"secondary"}
                    onClick={handleClose}
                    aria-label={translate("Close")}
                >
                    <Close16Icon />
                </IconButton>
            </StyledHeader>
            <StyledAppBanner>
                {logo}
                <StyledMetadataContainer>
                    <Typography variant={"h5"} component={"div"}>
                        {appName}
                    </Typography>
                    <Typography variant={"body1"} component={"div"} className={"chromatic-ignore"}>
                        {window.location.origin}
                    </Typography>
                </StyledMetadataContainer>
            </StyledAppBanner>
            <Stack direction={"column"} gap={3} padding={4} paddingX={3}>
                {children}
            </Stack>
        </Drawer>
    );
};

const StyledHeader = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 1, 2, 3),
    borderRadius: "16px 16px 0 0",
}));

const StyledAppBanner = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1.5),
    margin: theme.spacing(0, 3),
    padding: theme.spacing(2),
    backgroundColor: "rgba(0, 110, 250, 0.1)",
    borderRadius: theme.kaltura.shape.roundness2,
}));

const StyledMetadataContainer = styled("div")(fullWidthFlexItemStyles);
